import React from 'react';
import './Terms.css';

export default function TermsPage(): React.ReactElement {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <header className="terms-header">
          <h1 className="terms-title">Hereabout - Terms and Conditions</h1>
          <p className="terms-date">Last Updated: 11/04/2025</p>
        </header>

        <section className="terms-section">
          <h2>Quick Summary</h2>
          <p>
            <strong>The Basics:</strong> Hereabout lets you share location-based posts and join
            communities. You own your content, but we can use public posts for
            marketing. Be respectful, follow community rules, and you're good
            to go.
          </p>
          <p>
            <strong>Age:</strong> 18+ only. We'll deactivate accounts of younger users.
          </p>
          <p>
            <strong>Your Content:</strong>
          </p>
          <ul>
            <li>Public posts = everyone sees, we can use for marketing</li>
            <li>Following-only posts = only your followers see</li>
            <li>Private posts = only you see</li>
          </ul>
          <p>
            <strong>Data:</strong> We store everything on EU servers with strong
            privacy protections.
          </p>
        </section>

        <section className="terms-section">
          <h2>What This Agreement Covers</h2>
          <p>
            By using Hereabout ("the App"), developed and operated by
            Stormbyte LLC, you agree to these terms. If you don't agree with
            these terms, please don't use the App.
          </p>
          <p>
            We may update these terms from time to time - we'll notify
            you when we do.
          </p>
        </section>

        <section className="terms-section">
          <h2>Your Account & Responsibilities</h2>
          <p>
            <strong>The Simple Version:</strong> Create an account, keep it secure, don't be a
            jerk.
          </p>
          <p><strong>The Legal Details:</strong></p>
          <ul>
            <li>You're responsible for your account security and everything done with it</li>
            <li>Notify us immediately if someone else accesses your account</li>
            <li>You control your privacy settings - use them if you want privacy</li>
            <li>One person, one account (no ban evasion)</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>How the App Works</h2>
          <p><strong>What You Can Do:</strong></p>
          <ul>
            <li>Drop pins with photos, text, or voice recordings at your location</li>
            <li>Join communities ("Layers") based on shared interests</li>
            <li>Livestream temporarily (disappears when you stop)</li>
            <li>Set privacy levels for all your content</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>Content Moderation & Community Standards</h2>
          <p><strong>We Have No Tolerance for Objectionable Content or Abusive Users</strong></p>

          <h3>Strictly Prohibited:</h3>
          <ul>
            <li>Objectionable, offensive, or inappropriate content</li>
            <li>Abusive, harassing, or threatening behavior</li>
            <li>Hate speech, discriminatory, or defamatory content</li>
            <li>Illegal activities or content</li>
            <li>Violations of others' copyrights or privacy</li>
            <li>Impersonation or fake accounts</li>
            <li>Attempts to hack or exploit the app</li>
          </ul>

          <h3>Enforcement:</h3>
          <p>
            We have no tolerance for violations. Offenders will face immediate consequences including:
          </p>
          <ul>
            <li>Content removal</li>
            <li>Account suspension</li>
            <li>Permanent ban</li>
          </ul>

          <h3>Reporting:</h3>
          <p>
            Users can report objectionable content or abusive behavior directly through the app.
            All reports are reviewed by our moderation team.
          </p>
        </section>

        <section className="terms-section">
          <h2>Child Safety Standards</h2>
          <p>
            Hereabout and Stormbyte LLC strictly prohibit any form of Child Sexual Abuse Material (CSAM) or Child Sexual Abuse and Exploitation (CSAE). We do not permit the sharing, creation, or discovery of content that exploits or harms minors in any way.
          </p>

          <h3>Our Commitments:</h3>
          <ul>
            <li>We maintain zero tolerance for CSAM and child exploitation.</li>
            <li>Any reported or detected content of this nature is immediately removed and reported to the National Center for Missing and Exploited Children (NCMEC) and relevant law enforcement authorities.</li>
            <li>We follow <a href="https://support.google.com/googleplay/android-developer/answer/9893335" target="_blank" rel="noopener noreferrer">Google Play's Child Safety Standards</a> and other applicable laws and regulations protecting minors.</li>
            <li>Our team proactively reviews reports of suspicious or harmful content, and users found violating these standards are permanently banned.</li>
          </ul>

          <h3>Report Violations:</h3>
          <p>
            If you believe someone is in danger or that any content violates these standards, please report it immediately:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></li>
            <li><strong>Subject:</strong> Child Safety Report</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>Your Content & Our Rights</h2>
          <p>You own everything you post. We're just the platform.</p>

          <h3>Public Posts - You give us permission to:</h3>
          <ul>
            <li>Show it to other users in the app</li>
            <li>Use it in our marketing materials</li>
            <li>Include it in promotional content about the app</li>
            <li>Make technical backups</li>
          </ul>

          <h3>Following-Only & Private Posts - We can only:</h3>
          <ul>
            <li>Show it to your intended audience</li>
            <li>Make technical backups for app functionality</li>
          </ul>

          <h3>Layer Governance:</h3>
          <p>
            Community hosts are stewards who manage their spaces, but individual users
            retain ownership of their content.
          </p>

          <p>
            <strong>Important:</strong> These permissions end when you delete your
            content or account.
          </p>

          <p>
            <strong>Copyright Protection:</strong> Got a copyright issue? Email us at{' '}
            <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a>
          </p>
        </section>

        <section className="terms-section">
          <h2>Location & Privacy</h2>
          <p>
            <strong>The Simple Version:</strong> We only collect your location when you drop a
            pin. You control who sees it through privacy settings.
          </p>

          <h3>What We Collect:</h3>
          <ul>
            <li>GPS coordinates when you create location-based posts</li>
            <li>Historical location data associated with your posts</li>
          </ul>

          <h3>What We Don't Do:</h3>
          <ul>
            <li>Track your location continuously</li>
            <li>Share your location data with advertisers</li>
            <li>Sell your location information</li>
          </ul>

          <h3>Your Control:</h3>
          <ul>
            <li>Choose privacy level for each post</li>
            <li>Delete individual posts and their location data</li>
            <li>Delete all your location data by deleting your account</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>Communities ("Layers")</h2>
          <p>
            <strong>How It Works:</strong> You can create communities or join existing ones.
            Communities can be global (i.e. Foodies) or regional
            (geographically defined and enforced, i.e. CentralParkNYC)
          </p>

          <h3>The Host/Guest System:</h3>
          <ul>
            <li>Create a Layer = You're the host with full control</li>
            <li>Join someone else's Layer = You're a guest, follow their rules</li>
          </ul>

          <h3>Layer Governance & Data Ownership:</h3>
          <ul>
            <li>Layer hosts become de facto moderators with community management privileges</li>
            <li>Can assign additional moderators to help manage the community</li>
            <li>Can remove posts that violate their community-specific rules</li>
            <li>Can ban users who repeatedly break layer rules</li>
            <li>Act as stewards and hosts of their community space</li>
          </ul>

          <h3>Critical Distinction - Data Ownership:</h3>
          <ul>
            <li>Layer hosts DO NOT own the underlying data in their communities</li>
            <li>Individual users retain ownership of their own posts and content</li>
            <li>Stormbyte LLC retains platform-level control and final authority</li>
            <li>Hosts are stewards, not owners - they manage and protect the community space</li>
          </ul>

          <p>
            <strong>Permanence:</strong> Once a Layer has posts, it can't be deleted.
            However, hosts can transfer ownership if needed.
          </p>
        </section>

        <section className="terms-section">
          <h2>Deleting Your Data</h2>
          <h3>Account Deletion Process:</h3>
          <ol>
            <li>Delete through app - Your posts disappear immediately for other users</li>
            <li>30 days - Complete deletion from our servers</li>
            <li>90 days max - Removal from all backups</li>
          </ol>

          <h3>What You Can Delete:</h3>
          <ul>
            <li>Individual posts and their location data</li>
            <li>Your entire account and all associated data</li>
            <li>Specific data by emailing <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></li>
          </ul>

          <p>
            <strong>Data Download:</strong> Want a copy of your data? Email{' '}
            <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a> -
            we'll send it within 30 days.
          </p>
        </section>

        <section className="terms-section">
          <h2>Privacy & Third Parties</h2>
          <h3>Our Promise:</h3>
          <ul>
            <li>We store all data on secure EU servers</li>
            <li>We don't sell your personal information</li>
            <li>We don't share your data for third-party marketing</li>
          </ul>

          <h3>Third-Party Services We Use:</h3>
          <ul>
            <li>Google Firebase for push notifications and login</li>
            <li>Cloud storage for backing up your content securely</li>
          </ul>

          <h3>Marketing Use:</h3>
          <ul>
            <li>Public posts only - may appear in our promotional materials</li>
            <li>Private content - never used for marketing</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>Legal Protections & Limits</h2>
          <p>
            <strong>Age Requirement:</strong> 18+ only. Accounts of users under 18 will be
            deactivated upon discovery.
          </p>

          <h3>What We're Not Responsible For:</h3>
          <ul>
            <li>Indirect damages from using the app</li>
            <li>Lost profits or business opportunities</li>
            <li>More than what you've paid us in the past 12 months</li>
          </ul>

          <h3>Dispute Resolution:</h3>
          <ul>
            <li>Governing Law: Pennsylvania</li>
            <li>Disputes: Resolved through arbitration</li>
            <li>No Class Actions: Individual disputes only</li>
          </ul>

          <h3>If You Cause Problems:</h3>
          <p>You agree to protect us from lawsuits arising from:</p>
          <ul>
            <li>Your content or behavior</li>
            <li>Your violation of these terms</li>
            <li>Your violation of others' rights</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>Contact & Changes</h2>
          <h3>Questions?</h3>
          <ul>
            <li>General: <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></li>
            <li>Privacy/Data: <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></li>
            <li>Copyright: <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></li>
          </ul>

          <p>
            <strong>Updates to These Terms:</strong> We'll notify you in the app when
            we make changes. Continued use of the app means you agree to the
            updated terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>Frequently Asked Questions</h2>

          <div className="faq-item">
            <h4>Can I delete my posts?</h4>
            <p>Yes. Individual posts or your entire account - your choice.</p>
          </div>

          <div className="faq-item">
            <h4>What happens to my private posts?</h4>
            <p>They stay private. We never use private or following-only posts for marketing.</p>
          </div>

          <div className="faq-item">
            <h4>Can Layer moderators delete my posts?</h4>
            <p>Yes, if your post violates their community rules. But you still own the content.</p>
          </div>

          <div className="faq-item">
            <h4>Where is my data stored?</h4>
            <p>EU servers with strong privacy protections, regardless of where you live.</p>
          </div>

          <div className="faq-item">
            <h4>What if I'm under 18?</h4>
            <p>Your account will be deactivated. The app requires users to be 18 or older.</p>
          </div>

          <div className="faq-item">
            <h4>Can I export my data?</h4>
            <p>Yes. Email <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a> and we'll send you everything within 30 days.</p>
          </div>
        </section>

        <section className="terms-section">
          <h2>Acceptance of Terms</h2>
          <p>
            By using Hereabout, you acknowledge that you have read,
            understood, and agree to be bound by these Terms and Conditions.
          </p>
        </section>

        <footer className="terms-footer">
          <p>These Terms and Conditions are effective as of 11/04/2025 and apply to all users globally.</p>
        </footer>
      </div>
    </div>
  );
}
